import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams, Link as RouterLink } from "react-router-dom";
import clsx from "clsx";

import { makeRequest } from "../../utils/request";
import { Button, EmptyState, Spinner, Text, View } from "../../components/base";
import { Icon } from "../../components/base/Icon";
import { FineTuningPipelineStepper } from "../../components/FineTuningPipelineStepper";
import { FineTuningDeploymentStatusPill } from "../../components/FineTuningDeploymentStatusPill";

type FineTuningDeployment = {
  id: string;
  status: string;
  phase: string | null;
  org_id: string;
  cluster: string;
  job_id: string | null;
  base_model: string;
  adapter_uri: string | null;
  model_uri: string;
  served_model_name: string;
  accelerator: string;
  replicas: number;
  endpoint_url: string | null;
  error: string | null;
  created_at: string;
  terminated_at: string | null;
};

type DeploymentEvent = {
  id: string;
  type: string;
  level: string;
  message: string;
  created_at: string;
};

const TERMINAL = new Set(["terminated", "failed"]);
const POLL_MS = 6000;
const ALL_ORGS = "all_orgs=true";

const COLUMNS = "1fr 1.2fr 1.2fr 1.5fr 1.3fr 0.7fr 0.9fr 0.9fr";

const asUtc = (iso: string) => (/[zZ]|[+-]\d\d:?\d\d$/.test(iso) ? iso : `${iso}Z`);

const relativeTime = (iso: string | null): string => {
  if (!iso) return "—";
  const ms = Date.now() - new Date(asUtc(iso)).getTime();
  const m = Math.floor(ms / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
};

const shortId = (id: string) => (id.length > 16 ? `${id.slice(0, 14)}…` : id);

export const AdminFineTuningDeploymentsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedId = searchParams.get("id");

  const [deployments, setDeployments] = useState<FineTuningDeployment[]>([]);
  const [registered, setRegistered] = useState<Set<string>>(new Set());
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (showSpinner = false) => {
    if (showSpinner) setIsLoading(true);
    try {
      // also pull the registry so we can mark which deployments back a released voicerun/<name>
      const [deps, models] = await Promise.all([
        makeRequest<{ data: FineTuningDeployment[] }>(`/v1/fine_tuning/deployments?${ALL_ORGS}&limit=200`),
        makeRequest<{ data: { name: string; status: string }[] }>(`/v1/fine_tuning/models?${ALL_ORGS}&limit=200`).catch(() => ({
          data: [] as { name: string; status: string }[],
        })),
      ]);
      setDeployments(deps.data);
      setRegistered(new Set(models.data.filter((m) => m.status === "active").map((m) => m.name)));
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load deployments");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    load(true);
  }, [load]);

  const hasActive = deployments.some((d) => !TERMINAL.has(d.status));

  // A deployment "backs" a released voicerun model only if it serves a registered name AND is still
  // live — a terminated deployment that merely shares the served name is not the backing, so it
  // shouldn't get the chip (otherwise a dead deploy looks like it's still serving the released model).
  const backsModel = (d: FineTuningDeployment) => registered.has(d.served_model_name) && !TERMINAL.has(d.status);
  useEffect(() => {
    if (!hasActive) return;
    const t = setInterval(() => load(false), POLL_MS);
    return () => clearInterval(t);
  }, [hasActive, load]);

  const selected = deployments.find((d) => d.id === selectedId) || null;

  return (
    <View className="h-full w-full bg-gray-50 dark:bg-slate-900">
      <div className={clsx("h-full w-full relative transition-all duration-300", selectedId && "pr-[440px]")}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <Text className="text-xl font-semibold">Fine-Tuning Deployments</Text>
            <Text className="text-sm text-gray-500 dark:text-gray-400">
              {deployments.length} {deployments.length === 1 ? "endpoint" : "endpoints"} · all orgs
            </Text>
            {hasActive && <Spinner className="!h-4 !w-4 !text-brand" />}
          </div>
          <Button
            variant="ghost"
            type="button"
            onClick={() => load(true)}
          >
            <Icon
              name="refresh"
              size={18}
              className="text-brand"
            />
          </Button>
        </div>

        <FineTuningPipelineStepper />

        <div
          className="grid border-b-2 border-gray-200 dark:border-gray-700 px-6"
          style={{ gridTemplateColumns: COLUMNS }}
        >
          {["Org", "Deployment", "Served as", "Cluster", "Base model", "GPU", "Status", "Created"].map((h) => (
            <span
              key={h}
              className="py-3 pr-2 text-sm font-semibold text-gray-700 dark:text-gray-200"
            >
              {h}
            </span>
          ))}
        </div>

        <div
          className="overflow-y-auto"
          style={{ height: "calc(100% - 110px)" }}
        >
          {isLoading ? (
            <View className="flex items-center justify-center py-20">
              <Spinner className="!text-brand" />
            </View>
          ) : error ? (
            <View className="flex items-center justify-center py-20">
              <Text className="text-error">{error}</Text>
            </View>
          ) : deployments.length === 0 ? (
            <EmptyState
              icon="rocket"
              title="No deployments yet"
              description="Endpoints created via `vr ft deploy` will appear here, across all organizations."
            />
          ) : (
            deployments.map((dep) => (
              <div
                key={dep.id}
                onClick={() => setSearchParams({ id: dep.id })}
                className={clsx(
                  "grid items-center px-6 border-b border-gray-100 dark:border-gray-800 cursor-pointer",
                  "hover:bg-gray-100 dark:hover:bg-gray-800",
                  selectedId === dep.id && "bg-gray-100 dark:bg-gray-800",
                )}
                style={{ gridTemplateColumns: COLUMNS }}
              >
                <span
                  className="py-3 pr-2 truncate text-sm text-gray-500 dark:text-gray-400"
                  title={dep.org_id}
                >
                  {dep.org_id}
                </span>
                <span
                  className="py-3 pr-2 truncate text-sm font-mono"
                  title={dep.id}
                >
                  {shortId(dep.id)}
                </span>
                <span
                  className="py-3 pr-2 flex items-center gap-1.5 truncate text-sm"
                  title={dep.served_model_name}
                >
                  <span className="truncate">{dep.served_model_name}</span>
                  {backsModel(dep) && (
                    <span
                      className="flex-shrink-0 rounded bg-brand/10 px-1.5 py-0.5 text-[10px] font-medium text-brand"
                      title={`Released as voicerun/${dep.served_model_name}`}
                    >
                      voicerun
                    </span>
                  )}
                </span>
                <span
                  className="py-3 pr-2 truncate text-sm text-gray-500 dark:text-gray-400"
                  title={dep.cluster}
                >
                  {dep.cluster}
                </span>
                <span
                  className="py-3 pr-2 truncate text-sm text-gray-500 dark:text-gray-400"
                  title={dep.base_model}
                >
                  {dep.base_model}
                </span>
                <span className="py-3 pr-2 truncate text-sm text-gray-500 dark:text-gray-400">{dep.accelerator}</span>
                <span className="py-3 pr-2">
                  <FineTuningDeploymentStatusPill
                    status={dep.status}
                    phase={dep.phase}
                  />
                </span>
                <span className="py-3 pr-2 truncate text-sm text-gray-500 dark:text-gray-400">{relativeTime(dep.created_at)}</span>
              </div>
            ))
          )}
        </div>
      </div>

      {selected && (
        <DeploymentDetailPanel
          deployment={selected}
          registered={backsModel(selected)}
          onClose={() => setSearchParams({})}
          onChanged={() => load(false)}
        />
      )}
    </View>
  );
};

type PanelProps = {
  deployment: FineTuningDeployment;
  registered: boolean;
  onClose: () => void;
  onChanged: () => void;
};

const DeploymentDetailPanel = ({ deployment, registered, onClose, onChanged }: PanelProps) => {
  const [events, setEvents] = useState<DeploymentEvent[]>([]);
  const [isTerminating, setIsTerminating] = useState(false);
  const idRef = useRef(deployment.id);
  idRef.current = deployment.id;

  const isTerminal = TERMINAL.has(deployment.status);

  const loadEvents = useCallback(async () => {
    try {
      const { data } = await makeRequest<{ data: DeploymentEvent[] }>(`/v1/fine_tuning/deployments/${deployment.id}/events?all_orgs=true`);
      if (idRef.current === deployment.id) setEvents(data);
    } catch {
      /* best-effort */
    }
  }, [deployment.id]);

  useEffect(() => {
    setEvents([]);
    loadEvents();
    if (isTerminal) return;
    const t = setInterval(loadEvents, POLL_MS);
    return () => clearInterval(t);
  }, [loadEvents, isTerminal]);

  const onTerminate = async () => {
    setIsTerminating(true);
    try {
      await makeRequest(`/v1/fine_tuning/deployments/${deployment.id}?all_orgs=true`, { method: "DELETE" });
      onChanged();
      loadEvents();
    } catch {
      /* surfaced via refresh */
    } finally {
      setIsTerminating(false);
    }
  };

  return (
    <div className="fixed right-0 top-0 h-screen w-[440px] bg-white dark:bg-gray-800 border-l border-gray-200 dark:border-gray-700 flex flex-col z-20">
      <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center gap-2">
          <Text className="text-base font-semibold">Deployment</Text>
          <FineTuningDeploymentStatusPill
            status={deployment.status}
            phase={deployment.phase}
          />
        </div>
        <Button
          variant="ghost"
          type="button"
          onClick={onClose}
        >
          <Icon
            name="close"
            size={18}
            className="text-gray-500"
          />
        </Button>
      </div>

      <div className="flex-1 overflow-y-auto p-5 space-y-5">
        <Section
          label="Deployment ID"
          value={deployment.id}
          mono
        />
        <Section
          label="Organization"
          value={deployment.org_id}
          mono
        />
        <Field
          label="Cluster"
          value={deployment.cluster}
        />
        <Field
          label="Served as"
          value={deployment.served_model_name}
        />
        {registered && (
          <div className="flex items-baseline justify-between gap-3">
            <Text className="flex-shrink-0 text-xs uppercase text-gray-400">Registered as</Text>
            <RouterLink
              to={`/admin/fine-tuning-models?name=${encodeURIComponent(deployment.served_model_name)}`}
              className="min-w-0 text-right"
              title="Open the released model"
            >
              <Text className="break-all text-sm text-brand hover:underline">voicerun/{deployment.served_model_name}</Text>
            </RouterLink>
          </div>
        )}
        <Field
          label="Base model"
          value={deployment.base_model || "—"}
        />
        <Field
          label="GPU"
          value={deployment.accelerator}
        />
        <Field
          label="Replicas"
          value={String(deployment.replicas)}
        />
        {deployment.job_id && (
          <Field
            label="Source job"
            value={deployment.job_id}
            mono
          />
        )}
        <Section
          label="Model URI"
          value={deployment.model_uri || "—"}
          mono
        />
        {deployment.endpoint_url && (
          <Section
            label="Endpoint"
            value={deployment.endpoint_url}
            mono
          />
        )}
        <Field
          label="Created"
          value={new Date(asUtc(deployment.created_at)).toLocaleString()}
        />
        {deployment.terminated_at && (
          <Field
            label="Terminated"
            value={new Date(asUtc(deployment.terminated_at)).toLocaleString()}
          />
        )}
        {deployment.error && (
          <div>
            <Text className="block text-xs uppercase text-gray-400 mb-1">Error</Text>
            <Text className="text-sm text-error whitespace-pre-wrap break-words">{deployment.error}</Text>
          </div>
        )}

        <div>
          <Text className="text-xs uppercase text-gray-400 mb-2">Events</Text>
          {events.length === 0 ? (
            <Text className="text-sm text-gray-400">No events yet.</Text>
          ) : (
            <div className="space-y-2">
              {events.map((e) => (
                <div
                  key={e.id}
                  className="flex gap-2 text-sm"
                >
                  <span
                    className={clsx(
                      "mt-1.5 h-1.5 w-1.5 rounded-full flex-shrink-0",
                      e.level === "error" ? "bg-error" : e.level === "warning" ? "bg-yellow-500" : "bg-info",
                    )}
                  />
                  <div className="min-w-0">
                    <Text className="block text-gray-800 dark:text-gray-200 break-words">{e.message}</Text>
                    <Text className="block text-xs text-gray-400 mt-0.5">{relativeTime(e.created_at)}</Text>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {!isTerminal && (
        <div className="p-4 border-t border-gray-200 dark:border-gray-700">
          <Button
            className="w-full"
            variant="ghost"
            type="button"
            onClick={onTerminate}
            isLoading={isTerminating}
          >
            <Text className="text-error">Terminate deployment</Text>
          </Button>
        </div>
      )}
    </div>
  );
};

const Field = ({ label, value, mono }: { label: string; value: string; mono?: boolean }) => (
  <div className="flex items-baseline justify-between gap-3">
    <Text className="text-xs uppercase text-gray-400 flex-shrink-0">{label}</Text>
    <Text className={clsx("text-sm text-right break-words", mono && "font-mono text-xs")}>{value}</Text>
  </div>
);

const Section = ({ label, value, mono }: { label: string; value: string; mono?: boolean }) => (
  <div>
    {/* the design-system Text is inline; `block` keeps the value on its own line under the label */}
    <Text className="block text-xs uppercase text-gray-400 mb-1">{label}</Text>
    <Text className={clsx("block text-sm break-all", mono && "font-mono text-xs")}>{value}</Text>
  </div>
);
